/**
 * Token Cost Page — 토큰 비용
 * - 월별 필터
 * - 리스트: 날짜 + 워크플로우별 집계 (클릭 시 팝업 상세)
 */

import { useState, useEffect, useCallback } from 'react';
import { Topbar } from '../components/layout';
import { Badge, Button, Modal, Pagination, SearchInput, Select, useToast } from '../components/ui';
import { logsApi, type TokenUsageLog } from '../api/logs';
import { authApi, tenantApi } from '../api';
import { isProductAdminRole } from '../utils/role';
import RefreshIcon from '../assets/icons/common/refresh.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import '../styles/adminPage.css';
// ── 유틸 ──────────────────────────────────────────────────────────────────────

function fmtDatetime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}. (${days[d.getDay()]})`;
}

function fmtModelName(modelId: string): string {
  if (!modelId || modelId === 'unknown') return modelId || '-';
  const m = modelId.toLowerCase();
  if (m.includes('haiku'))                       return 'Claude Haiku';
  if (m.includes('sonnet'))                      return 'Claude Sonnet';
  if (m.includes('opus'))                        return 'Claude Opus';
  if (m.includes('nova') && m.includes('micro')) return 'Nova Micro';
  if (m.includes('nova') && m.includes('lite'))  return 'Nova Lite';
  if (m.includes('nova') && m.includes('pro'))   return 'Nova Pro';
  if (m.includes('titan'))                       return 'Titan';
  if (m.includes('gemini'))                      return 'Gemini';
  if (m.includes('llama'))                       return 'Llama';
  if (m.includes('mistral'))                     return 'Mistral';
  // 패턴 미매칭 시 raw ID 그대로 표시
  return modelId;
}

function getLast12Months(): { value: string; label: string }[] {
  const result = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
    result.push({ value, label });
  }
  return result;
}

// ── 그룹핑 ────────────────────────────────────────────────────────────────────

interface ModelStat {
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
}

interface RowGroup {
  key: string;
  date: string;
  workflowName: string;
  userEmail: string;
  items: TokenUsageLog[];
  modelStats: ModelStat[];   // 모델별 집계 (순서 보장)
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  totalCostUsd: number;
}

function buildRows(logs: TokenUsageLog[]): RowGroup[] {
  const map = new Map<string, TokenUsageLog[]>();
  for (const log of logs) {
    const date = log.createdAt.slice(0, 10);
    const key = `${date}||${log.userId}||${log.workflowId || log.workflowName || ''}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(log);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, items]) => {
      const first = items[0]!;

      // 모델별 토큰 집계
      const statMap = new Map<string, ModelStat>();
      for (const l of items) {
        const prev = statMap.get(l.modelId) || { modelId: l.modelId, inputTokens: 0, outputTokens: 0, totalTokens: 0, costUsd: 0 };
        statMap.set(l.modelId, {
          modelId:      l.modelId,
          inputTokens:  prev.inputTokens  + l.inputTokens,
          outputTokens: prev.outputTokens + l.outputTokens,
          totalTokens:  prev.totalTokens  + l.totalTokens,
          costUsd:      prev.costUsd      + l.costUsd,
        });
      }

      return {
        key,
        date:         first.createdAt.slice(0, 10),
        workflowName: first.workflowName || '(워크플로우 없음)',
        userEmail:    first.userEmail || first.userId || '-',
        items: [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
        modelStats:   Array.from(statMap.values()),
        inputTokens:  items.reduce((s, l) => s + l.inputTokens, 0),
        outputTokens: items.reduce((s, l) => s + l.outputTokens, 0),
        totalTokens:  items.reduce((s, l) => s + l.totalTokens, 0),
        totalCostUsd: items.reduce((s, l) => s + l.costUsd, 0),
      };
    });
}

function calcSummary(logs: TokenUsageLog[]) {
  return {
    inputTokens:  logs.reduce((s, l) => s + l.inputTokens, 0),
    outputTokens: logs.reduce((s, l) => s + l.outputTokens, 0),
    totalTokens:  logs.reduce((s, l) => s + l.totalTokens, 0),
    totalCostUsd: logs.reduce((s, l) => s + l.costUsd, 0),
    count:        logs.length,
  };
}

/** 이전 달 같은 기간(1일 ~ 기준일) 로그 */
function prevMonthSamePeriod(logs: TokenUsageLog[], month: string, uptoDay: number): TokenUsageLog[] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const p = new Date(y, m - 2, 1);
  const prefix = `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}`;
  return logs.filter(l => l.createdAt.startsWith(prefix) && Number(l.createdAt.slice(8, 10)) <= uptoDay);
}

function pctDelta(cur: number, prev: number): number | null {
  if (!prev) return null;
  return Math.round((cur / prev - 1) * 100);
}

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
}

function fmtAxis(v: number): string {
  if (v >= 1_000_000) return `${+(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${+(v / 1000).toFixed(1)}K`;
  return String(Math.round(v));
}

const MODEL_COLORS = ['#7367e0', '#2f9fd6', '#e08a3c', '#3fb27f', '#d14381', '#8a8f9c'];
const PAGE_SIZE = 15;

const IconTok = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><ellipse cx="12" cy="6" rx="7" ry="3"/><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6"/></svg>
);
const IconUsd = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M15 9.5c-.5-1-1.6-1.5-3-1.5-1.7 0-3 .8-3 2s1.3 1.7 3 2 3 .8 3 2-1.3 2-3 2c-1.4 0-2.5-.5-3-1.5M12 6v2M12 16v2"/></svg>
);
const IconChat = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M4 5h16v11H9l-5 4z"/></svg>
);
const IconAvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 20h16M4 16l5-5 4 3 7-7"/></svg>
);
const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M4 21h16"/></svg>
);

function Delta({ v }: { v: number | null }) {
  if (v === null) return <>지난달 데이터 없음</>;
  return (
    <>지난달 대비<span className={v > 0 ? 'tc-up' : 'tc-down'}>{v > 0 ? '+' : v < 0 ? '−' : ''}{Math.abs(v)}%</span></>
  );
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function TokenCostPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const [logs, setLogs] = useState<TokenUsageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [page, setPage] = useState(0);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number; day: number } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [dailyView, setDailyView] = useState<'chart' | 'table'>('chart');

  const months = getLast12Months();
  const [selectedMonth, setSelectedMonth] = useState(months[0].value);

  // 상세 팝업
  const [detailRow, setDetailRow] = useState<RowGroup | null>(null);

  const currentUser = authApi.getUser();
  const isProductAdmin = isProductAdminRole(currentUser?.role);

  const [tenantFilter, setTenantFilter] = useState('');
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const [tenantNameMap, setTenantNameMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (isProductAdmin) tenantApi.list().then(ts => {
      setTenants(ts);
      setTenantNameMap(new Map(ts.map(t => [t.id, t.name])));
    }).catch(() => {});
  }, [isProductAdmin]);

  // 이메일 → 이름 매핑
  const [userNameMap, setUserNameMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    authApi.listUsers().then(users => {
      const map = new Map<string, string>();
      users.forEach(u => {
        if (u.email) map.set(u.email, u.name || u.email);
        if (u.id)    map.set(u.id,    u.name || u.email); // userId(UUID)로 저장된 경우 대응
      });
      setUserNameMap(map);
    }).catch(() => {});
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await logsApi.getTokenUsage({ limit: 1000, tenantId: tenantFilter || undefined });
      setLogs(data);
      setLoadedAt(new Date());
    } catch {
      showToast('error', '토큰 사용량을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showToast, tenantFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (log: TokenUsageLog, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(log.logId);
    try {
      await logsApi.deleteTokenUsage(log.logId, log.userId);
      setLogs(prev => prev.filter(l => l.logId !== log.logId));
      if (detailRow) {
        const updatedItems = detailRow.items.filter(l => l.logId !== log.logId);
        if (updatedItems.length === 0) setDetailRow(null);
        else setDetailRow({ ...detailRow, items: updatedItems,
          inputTokens:  updatedItems.reduce((s, l) => s + l.inputTokens, 0),
          outputTokens: updatedItems.reduce((s, l) => s + l.outputTokens, 0),
          totalTokens:  updatedItems.reduce((s, l) => s + l.totalTokens, 0),
          totalCostUsd: updatedItems.reduce((s, l) => s + l.costUsd, 0),
        });
      }
      showToast('success', '삭제됐습니다.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '삭제에 실패했습니다.';
      showToast('error', msg);
    } finally {
      setDeletingId(null);
    }
  };

  const now = new Date();
  const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selY, selM] = selectedMonth.split('-').map(Number) as [number, number];
  const daysInMonth = new Date(selY, selM, 0).getDate();
  const isCurrentMonth = selectedMonth === curMonth;
  const uptoDay = isCurrentMonth ? now.getDate() : daysInMonth;

  const userKey = (l: TokenUsageLog) => l.userEmail || l.userId;
  const userLabel = (u: string) => userNameMap.get(u) || u;

  const monthLogs = logs.filter(l => l.createdAt.startsWith(selectedMonth));
  const userOptions  = Array.from(new Set(monthLogs.map(userKey))).sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
  const modelOptions = Array.from(new Set(monthLogs.map(l => fmtModelName(l.modelId)))).sort();
  const modelColor = (rawId: string) => MODEL_COLORS[Math.max(0, modelOptions.indexOf(fmtModelName(rawId))) % MODEL_COLORS.length];

  const filtered = monthLogs.filter(l => {
    if (filterUser && userKey(l) !== filterUser) return false;
    if (filterModel && fmtModelName(l.modelId) !== filterModel) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      l.workflowName?.toLowerCase().includes(q) ||
      l.userEmail?.toLowerCase().includes(q) ||
      userLabel(userKey(l)).toLowerCase().includes(q) ||
      fmtModelName(l.modelId).toLowerCase().includes(q)
    );
  });

  const summary = calcSummary(monthLogs);
  const prev    = calcSummary(prevMonthSamePeriod(logs, selectedMonth, uptoDay));
  const forecast = uptoDay ? summary.totalCostUsd / uptoDay * daysInMonth : 0;
  const inPct  = summary.totalTokens ? Math.round(summary.inputTokens / summary.totalTokens * 100) : 0;

  const rows    = buildRows(filtered);
  const pages   = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const curPage = Math.min(page, pages - 1);
  const pageRows = rows.slice(curPage * PAGE_SIZE, curPage * PAGE_SIZE + PAGE_SIZE);
  const rowsSum = calcSummary(filtered);

  // 일별 차트
  // 월 전체 일수로 고정 (월초에도 막대 폭 일정, 아직 안 온 날은 빈 칸)
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const byDay = new Map<number, { tin: number; tout: number; cost: number }>();
  for (const l of monthLogs) {
    const d = Number(l.createdAt.slice(8, 10));
    const g = byDay.get(d) || { tin: 0, tout: 0, cost: 0 };
    g.tin += l.inputTokens; g.tout += l.outputTokens; g.cost += l.costUsd;
    byDay.set(d, g);
  }
  const dayVal = (d: number) => byDay.get(d) || { tin: 0, tout: 0, cost: 0 };
  const yMax = niceMax(Math.max(0, ...days.map(d => dayVal(d).tin + dayVal(d).tout)) * 1.05);
  const dayStr = (d: number) => `${selY}-${String(selM).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const resetPage = () => setPage(0);

  const exportCsv = () => {
    const head = ['날짜', '워크플로우', '사용자', '모델', '대화 수', '입력 토큰', '출력 토큰', '총 토큰', '비용 (USD)'];
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [head, ...rows.map(r => [
      r.date, r.workflowName, userLabel(r.userEmail), r.modelStats.map(s => s.modelId).join(' / '),
      r.items.length, r.inputTokens, r.outputTokens, r.totalTokens, r.totalCostUsd.toFixed(4),
    ])].map(cols => cols.map(esc).join(','));
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `token-cost-${selectedMonth}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const toolbar = (
    <div className="dash-updated tc-toolbar">
      {loadedAt && <span>{`${loadedAt.toLocaleDateString('ko-KR')} ${String(loadedAt.getHours()).padStart(2, '0')}:${String(loadedAt.getMinutes()).padStart(2, '0')} 기준`}</span>}
      <Select
        value={selectedMonth}
        onChange={e => { setSelectedMonth(e.target.value); setFilterUser(''); setFilterModel(''); resetPage(); }}
        options={months}
      />
      {isProductAdmin && (
        <Select
          value={tenantFilter}
          onChange={e => { setTenantFilter(e.target.value); setFilterUser(''); setFilterModel(''); resetPage(); }}
        >
          <option value="">전체 테넌트</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      )}
      <Button color="gray" appearance="outline" size="md" onClick={() => load(true)} disabled={refreshing}>
        <RefreshIcon className={refreshing ? 'dash-spin' : undefined} />
        새로고침
      </Button>
      <Button color="gray" appearance="outline" size="md" onClick={exportCsv} disabled={rows.length === 0}>
        <IconDownload />
        CSV 내보내기
      </Button>
    </div>
  );

  const kpis: { icon: React.ReactNode; label: string; value: string; unit?: string; viz?: React.ReactNode; cap: React.ReactNode }[] = [
    {
      icon: <IconTok />, label: '이번 달 총 토큰', value: summary.totalTokens.toLocaleString(), unit: '개',
      viz: summary.totalTokens > 0 && (
        <>
          <i style={{ flex: summary.inputTokens, background: 'var(--primary)' }} />
          <i style={{ flex: summary.outputTokens, background: 'var(--info)' }} />
        </>
      ),
      cap: summary.totalTokens ? `입력 ${inPct}% · 출력 ${100 - inPct}%` : '사용 기록 없음',
    },
    {
      icon: <IconUsd />, label: '이번 달 추정 비용', value: `$${summary.totalCostUsd.toFixed(2)}`,
      viz: (
        <>
          <i style={{ flex: uptoDay, background: 'var(--primary)' }} />
          <i style={{ flex: daysInMonth - uptoDay, background: 'transparent' }} />
        </>
      ),
      cap: isCurrentMonth ? `월말 예상 $${forecast.toFixed(2)}` : `${daysInMonth}일 전체 기준`,
    },
    {
      icon: <IconChat />, label: '대화 수', value: summary.count.toLocaleString(), unit: '건',
      cap: <Delta v={pctDelta(summary.count, prev.count)} />,
    },
    {
      icon: <IconAvg />, label: '비용 (USD)', value: `$${summary.totalCostUsd.toFixed(4)}`,
      cap: <Delta v={pctDelta(summary.totalCostUsd, prev.totalCostUsd)} />,
    },
  ];

  const fmtUser = (u: string) => {
    const name = userNameMap.get(u);
    if (name) return <span title={u}>{name}</span>;
    if (u.length > 20 && !u.includes('@')) return <span className="mono" title={u}>{u.slice(0, 8)}…{u.slice(-4)}</span>;
    return <span title={u}>{u}</span>;
  };

  const body = (
    <div className="dash tc-page">
      {embedded && toolbar}

      {/* KPI */}
      <section className="dash-stats">
        {kpis.map(k => (
          <div className="item-card dash-stat tc-stat" key={k.label}>
            <span className="item-card-icon item-card-icon--primary">{k.icon}</span>
            <div className="item-card-body">
              <div className="dash-stat-label">{k.label}</div>
              <div className="dash-stat-value">{k.value}{k.unit && <small>{k.unit}</small>}</div>
              <div className="dash-bar">{k.viz}</div>
              <p className="dash-stat-note">{k.cap}</p>
            </div>
          </div>
        ))}
      </section>

      {/* 일별 토큰 사용량 */}
      <div className="tc-list">
      <div className="section-label tc-section-head">
        <span className="tc-section-title">
          일별 토큰 사용량
          <Badge color="primary" shape="tag">{selY}년 {selM}월</Badge>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {dailyView === 'chart' && (
            <div className="dash-legend">
              <span style={{ '--c': 'var(--primary)' } as React.CSSProperties}>입력 토큰</span>
              <span style={{ '--c': 'var(--info)' } as React.CSSProperties}>출력 토큰</span>
            </div>
          )}
          <div className="tc-view-toggle">
            <button
              className={`tc-view-btn${dailyView === 'chart' ? ' active' : ''}`}
              onClick={() => setDailyView('chart')}
              title="차트로 보기"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="12" width="4" height="9"/><rect x="10" y="6" width="4" height="15"/><rect x="17" y="3" width="4" height="18"/></svg>
              차트
            </button>
            <button
              className={`tc-view-btn${dailyView === 'table' ? ' active' : ''}`}
              onClick={() => setDailyView('table')}
              title="표로 보기"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>
              표
            </button>
          </div>
        </div>
      </div>
      <div className="dash-card">
        {dailyView === 'chart' ? (
          <div className="tc-chart">
            <div className="tc-yax">
              {[4, 3, 2, 1, 0].map(i => <span key={i}>{fmtAxis(yMax * i / 4)}</span>)}
            </div>
            <div className="tc-plot">
              {days.map(d => {
                const v = dayVal(d);
                if (d > uptoDay) return <div key={d} className="tc-col future" />;
                return (
                  <div
                    key={d}
                    className="tc-col"
                    onMouseMove={e => setTip({ x: e.clientX, y: e.clientY, day: d })}
                    onMouseLeave={() => setTip(null)}
                  >
                    <i style={{ height: `${v.tin / yMax * 100}%`, background: 'var(--primary)' }} />
                    <i style={{ height: `${v.tout / yMax * 100}%`, background: 'var(--info)' }} />
                  </div>
                );
              })}
            </div>
            <div className="tc-xax">
              {days.map(d => (
                <span key={d} className={isCurrentMonth && d === uptoDay ? 'today' : d > uptoDay ? 'future' : undefined}>
                  {isCurrentMonth && d === uptoDay ? '오늘' : `${selM}.${d}`}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="left">날짜</th>
                  <th className="right">입력 토큰</th>
                  <th className="right">출력 토큰</th>
                  <th className="right">총 토큰</th>
                  <th className="right">비용 (USD)</th>
                </tr>
              </thead>
              <tbody>
                {days.filter(d => d <= uptoDay && (dayVal(d).tin + dayVal(d).tout) > 0).map(d => {
                  const v = dayVal(d);
                  const total = v.tin + v.tout;
                  return (
                    <tr key={d}>
                      <td className="left mono mute">{fmtDate(dayStr(d))}</td>
                      <td className="right mono">{v.tin.toLocaleString()}</td>
                      <td className="right mono">{v.tout.toLocaleString()}</td>
                      <td className="right mono">{total.toLocaleString()}</td>
                      <td className="right mono">${v.cost.toFixed(4)}</td>
                    </tr>
                  );
                })}
                {days.every(d => d > uptoDay || (dayVal(d).tin + dayVal(d).tout) === 0) && (
                  <tr><td colSpan={5} className="tc-empty-row">이 달의 사용 기록이 없습니다.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="tc-total">
                  <td className="left">합계</td>
                  <td className="right mono">{days.reduce((s, d) => s + dayVal(d).tin, 0).toLocaleString()}</td>
                  <td className="right mono">{days.reduce((s, d) => s + dayVal(d).tout, 0).toLocaleString()}</td>
                  <td className="right mono">{days.reduce((s, d) => s + dayVal(d).tin + dayVal(d).tout, 0).toLocaleString()}</td>
                  <td className="right mono">${days.reduce((s, d) => s + dayVal(d).cost, 0).toFixed(4)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      </div>
      {tip && (() => {
        const v = dayVal(tip.day);
        return (
          <div className="tc-tip" style={{ left: Math.min(tip.x + 14, window.innerWidth - 200), top: tip.y + 14 }}>
            <b>{fmtDate(dayStr(tip.day))}</b><br />
            입력 {v.tin.toLocaleString()} · 출력 {v.tout.toLocaleString()} · 총 {(v.tin + v.tout).toLocaleString()}<br />
            비용 ${v.cost.toFixed(4)}
          </div>
        );
      })()}

      {/* 사용 내역 */}
      <div className="tc-list">
      <div className="section-label">
        <span>사용 내역</span>
      </div>

      <div className="tc-list-search">
        <Select value={filterUser} onChange={e => { setFilterUser(e.target.value); resetPage(); }}>
          <option value="">전체 사용자</option>
          {userOptions.map(u => <option key={u} value={u}>{userLabel(u)}</option>)}
        </Select>
        <Select value={filterModel} onChange={e => { setFilterModel(e.target.value); resetPage(); }}>
          <option value="">전체 모델</option>
          {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
        </Select>
        <div className="tc-search">
          <SearchInput
            placeholder="워크플로우명, 사용자, 모델로 검색..."
            value={search}
            onChange={e => { setSearch(e.target.value); resetPage(); }}
            onClear={() => { setSearch(''); resetPage(); }}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">로딩 중...</div>
      ) : rows.length === 0 ? (
        <div className="logs-empty">
          <p className="logs-empty-title">{monthLogs.length === 0 ? '사용 내역이 없습니다' : '검색 결과가 없습니다'}</p>
          {monthLogs.length === 0 && <p className="logs-empty-desc">챗봇 대화가 발생하면 여기에 기록됩니다.</p>}
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>날짜</th>
                  <th>워크플로우</th>
                  <th>사용자</th>
                  {isProductAdmin && !tenantFilter && <th>테넌트</th>}
                  <th>모델</th>
                  <th>대화 수</th>
                  <th>입력 토큰</th>
                  <th>출력 토큰</th>
                  <th>총 토큰</th>
                  <th>비용 (USD)</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(row => (
                  <tr
                    key={row.key}
                    className="token-day-row"
                    onClick={() => setDetailRow(row)}
                    style={{ cursor: 'pointer' }}
                    title="클릭하여 상세 보기"
                  >
                    <td className="left mono mute">{fmtDate(row.date)}</td>
                    <td className="left"><strong>{row.workflowName}</strong></td>
                    <td className="left mute">{fmtUser(row.userEmail)}</td>
                    {isProductAdmin && !tenantFilter && (
                      <td className="left mute" style={{ fontSize: 12 }}>
                        {row.items[0]?.tenantId ? (tenantNameMap.get(row.items[0].tenantId) || row.items[0].tenantId) : '-'}
                      </td>
                    )}
                    <td className="left">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        {row.modelStats.map(s => (
                          <span key={s.modelId} className="tc-mdl" title={fmtModelName(s.modelId)}
                            style={{ '--c': modelColor(s.modelId) } as React.CSSProperties}>
                            {s.modelId}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td><Badge color="gray" shape="tag">{row.items.length}건</Badge></td>
                    <td className="right mono">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
                        {row.modelStats.length > 1
                          ? row.modelStats.map(s => <span key={s.modelId}>{s.inputTokens.toLocaleString()}</span>)
                          : row.inputTokens.toLocaleString()}
                      </div>
                    </td>
                    <td className="right mono">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
                        {row.modelStats.length > 1
                          ? row.modelStats.map(s => <span key={s.modelId}>{s.outputTokens.toLocaleString()}</span>)
                          : row.outputTokens.toLocaleString()}
                      </div>
                    </td>
                    <td className="right mono">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
                        {row.modelStats.length > 1
                          ? row.modelStats.map(s => <span key={s.modelId}>{s.totalTokens.toLocaleString()}</span>)
                          : row.totalTokens.toLocaleString()}
                      </div>
                    </td>
                    <td className="right mono">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
                        {row.modelStats.length > 1
                          ? row.modelStats.map(s => <span key={s.modelId}><strong>${s.costUsd.toFixed(4)}</strong></span>)
                          : <strong>${row.totalCostUsd.toFixed(4)}</strong>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="tc-total">
                  <td colSpan={isProductAdmin && !tenantFilter ? 5 : 4} className="left">합계 ({rows.length}건)</td>
                  <td>{rowsSum.count.toLocaleString()}건</td>
                  <td className="right mono">{rowsSum.inputTokens.toLocaleString()}</td>
                  <td className="right mono">{rowsSum.outputTokens.toLocaleString()}</td>
                  <td className="right mono">{rowsSum.totalTokens.toLocaleString()}</td>
                  <td className="right mono">${rowsSum.totalCostUsd.toFixed(4)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {pages > 1 && (
            <Pagination
              current={curPage + 1}
              total={pages}
              onChange={p => setPage(p - 1)}
              info={<><b>{curPage * PAGE_SIZE + 1}–{Math.min(rows.length, curPage * PAGE_SIZE + PAGE_SIZE)}</b> / {rows.length}건</>}
            />
          )}
        </>
      )}
      </div>

      {/* 건별 상세 팝업 */}
      {detailRow && (() => {
        // 워크플로우 실행 vs 챗봇 대화 구분
        const isWorkflow = !detailRow.items[0]?.chatSessionId && !!detailRow.items[0]?.workflowId;
        // tfoot colspan: 합계 레이블이 덮는 열 수 (입력/출력 앞까지)
        // workflow: 시각+사용자+모델+실행쿼리+결과요약+입력+출력 = 7
        // chatbot:  시각+SessionID+ChatID+사용자+모델+질문+답변+입력+출력 = 9
        const tfootSpan = isWorkflow ? 7 : 9;

        return (
          <Modal
            isOpen
            onClose={() => setDetailRow(null)}
            title={`${fmtDate(detailRow.date)} · ${detailRow.workflowName}`}
            size="lg"
            footerRight={
              <Button color="gray" appearance="outline" onClick={() => setDetailRow(null)}>닫기</Button>
            }
          >
            <div className='form-label'>
              {isWorkflow ? (
                <><Badge color="info">워크플로우</Badge>자동 실행</>
              ) : (
                <><Badge color="medium">챗봇</Badge>대화</>
              )}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th className="left">시각</th>
                    {!isWorkflow && <th className="left">Session ID</th>}
                    {!isWorkflow && <th className="left">Chat ID</th>}
                    <th className="left">사용자</th>
                    <th className="left">모델</th>
                    <th className="left">{isWorkflow ? '실행 쿼리' : '질문'}</th>
                    <th className="left">{isWorkflow ? '결과 요약' : '답변'}</th>
                    <th>입력</th>
                    <th>출력</th>
                    <th>합계</th>
                    <th>비용 ($)</th>
                    {isProductAdmin && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {detailRow.items.map(log => (
                    <tr key={log.logId}>
                      <td className="left mono mute">{fmtDatetime(log.createdAt)}</td>
                      {!isWorkflow && (
                        <td className="left mono mute" style={{ fontSize: 11, wordBreak: 'break-all' }}
                          title={log.chatSessionId || ''}>
                          {log.chatSessionId || '-'}
                        </td>
                      )}
                      {!isWorkflow && (
                        <td className="left mono mute" style={{ fontSize: 11, wordBreak: 'break-all' }}
                          title={log.chatId || ''}>
                          {log.chatId ? `#${log.chatId}` : '-'}
                        </td>
                      )}
                      <td className="left mute"
                        title={log.userEmail || log.userId}>
                        {userNameMap.get(log.userEmail || '') || log.userEmail || log.userId}
                      </td>
                      <td className="left">
                        <Badge variant='type'>{log.modelId}</Badge>
                      </td>
                      <td className="left" style={{ maxWidth: 200, fontSize: 12 }}>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 72, overflow: 'hidden' }}
                          title={log.question}>
                          {log.question || '-'}
                        </div>
                      </td>
                      <td className="left" style={{ maxWidth: 280, fontSize: 12 }}>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 72, overflow: 'hidden' }}
                          title={log.answer}>
                          {log.answer ? (log.answer.length > 150 ? log.answer.slice(0, 150) + '…' : log.answer) : '-'}
                        </div>
                      </td>
                      <td className="mono mute">{log.inputTokens.toLocaleString()}</td>
                      <td className="mono mute">{log.outputTokens.toLocaleString()}</td>
                      <td className="mono">{log.totalTokens.toLocaleString()}</td>
                      <td className="mono">${log.costUsd.toFixed(4)}</td>
                      {isProductAdmin && (
                        <td>
                          <button
                            className="icon-action-btn delete"
                            title="삭제"
                            disabled={deletingId === log.logId}
                            onClick={e => handleDelete(log, e)}
                          >
                            <TrashIcon />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={tfootSpan} className="left" style={{ fontWeight: 600, paddingTop: 12 }}>합계</td>
                    <td className="mono" style={{ fontWeight: 600 }}>{detailRow.totalTokens.toLocaleString()}</td>
                    <td className="mono" style={{ fontWeight: 600 }}>${detailRow.totalCostUsd.toFixed(4)}</td>
                    {isProductAdmin && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          </Modal>
        );
      })()}
    </div>
  );

  if (embedded) return body;

  return (
    <>
      <Topbar title="토큰 비용" description="AI 모델 토큰 사용량과 비용을 조회합니다" actions={toolbar} />
      <div className="page-content">{body}</div>
    </>
  );
}

export default TokenCostPage;
